import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import {
  closeInnerPopup,
  closePopup,
  openInnerPopup,
  openPopup,
} from '../../../data/providers/popupsProvider';
import { Level } from '../../../data/types';
import { Row } from '../../layout/Row';
import { CloseButton, IconButton, SaveButton } from '@ui/shared/Button';
import { CardBody } from '../card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../card/CardHeader';

import { ControlSpacer } from '../form/ControlSpacer';
import { FormContainer } from '../form/FormContainer';
import { TextArea } from '../form/TextArea';
import {
  InnerPopupCard,
  PopupCard,
  PopupCardContent,
} from '../popups/PopupCard';

interface IPopupProps {
  onClose: () => void;
  currentNote: string;
  setNote: (note: string) => void;
  level?: number;
}

const EditingPopup = (props: IPopupProps): React.ReactElement => {
  const [note, setNote] = React.useState<string>(props.currentNote ?? '');
  const onSubmit = (): void => {
    props.setNote(note);
    props.onClose();
  };

  const Card = props.level === 1 ? InnerPopupCard : PopupCard;

  return (
    <Card onClose={props.onClose} noClickOutside size='small'>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Edit Notes</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          <FormContainer>
            <TextArea
              name='notes'
              label='Notes'
              value={note}
              onChange={setNote}
            />
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton onClick={onSubmit} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </Card>
  );
};

interface IProps extends RouteComponentProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  pdf?: boolean;
  level?: Level;
  borderBottom?: boolean;
}

export const Notable = withRouter((props: IProps): React.ReactElement => {
  const [note, setNote] = React.useState<string>('');
  const editNote = (): void => {
    if (props.level === 1) {
      openInnerPopup(
        <EditingPopup
          onClose={closeInnerPopup(props.history)}
          currentNote={note}
          setNote={setNote}
          level={props.level}
        />,
        props.history,
        'edit-note'
      );
    } else {
      openPopup(
        <EditingPopup
          onClose={closePopup(props.history)}
          currentNote={note}
          setNote={setNote}
          level={props.level}
        />,
        props.history,
        'edit-note'
      );
    }
  };

  const clearNote = (): void => setNote('');
  return props.pdf ? (
    <NotesContainer style={props.style} className='avoid-break'>
      {props.children}
      {note?.length ? (
        <Row
          style={{
            paddingLeft: '10px',
            borderBottom: props.borderBottom
              ? '1px solid var(--color-separator)'
              : 'none',
          }}
        >
          <ControlSpacer>
            <NotesLabel>
              <span style={{ fontWeight: 'bold' }}>Notes:</span>
              <EditContainer data-html2canvas-ignore='true'>
                <IconButton
                  title='Edit Note'
                  style={{ marginLeft: '10px' }}
                  icon='edit'
                  onClick={editNote}
                />
                <IconButton
                  icon='clear'
                  onClick={clearNote}
                  title='Remove Note'
                />
              </EditContainer>
            </NotesLabel>
            <p>{note}</p>
          </ControlSpacer>
        </Row>
      ) : (
        <TriggerContainer
          data-html2canvas-ignore='true'
          className='avoid-break'
          borderBottom={props.borderBottom}
        >
          <ControlSpacer data-html2canvas-ignore='true'>
            <IconButton
              icon='edit'
              onClick={editNote}
              title='Add Notes'
            />
          </ControlSpacer>
        </TriggerContainer>
      )}
    </NotesContainer>
  ) : (
    <React.Fragment>{props.children}</React.Fragment>
  );
});

const NotesContainer = styled.div`
  width: 100%;
`;

const TriggerContainer = styled.div`
  display: flex;
  justify-content: center;
  border-bottom: ${(props: { borderBottom?: boolean }): string =>
    props.borderBottom ? '1px solid var(--color-separator)' : 'none'};
  padding-bottom: ${(props: { borderBottom?: boolean }): string =>
    props.borderBottom ? '10px' : '0px'};
  @media print {
    display: none;
  }
`;

const NotesLabel = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 5px;
`;

const EditContainer = styled.div`
  margin-left: 5px;
  display: flex;
  flex-direction: row;
`;
